import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  animate,
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  MotionValue,
} from 'framer-motion';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  getContrastTextColor,
  getContrastRatio,
  hslToHex,
  hexToHsl,
  isValidHex,
  normalizeHex,
} from '../../../utils/theme/brandTheme';

/**
 * ==============   Utils & Math   ================
 */

function calculateAngle(index: number, totalInRing: number): number {
  return (index / totalInRing) * Math.PI * 2;
}

function calculateBasePosition(angle: number, radius: number) {
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
}

function calculateHue(angle: number): number {
  const hueDegrees = (angle * 180) / Math.PI - 90 - 180;
  return ((hueDegrees % 360) + 360) % 360;
}

/**
 * Custom pointer position tracker using MotionValues
 */
function usePointerPosition() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent | MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, [x, y]);

  return { x, y };
}

interface SpringConfig {
  damping: number;
  stiffness: number;
}

interface ColorDotProps {
  ring: number;
  index: number;
  totalInRing: number;
  centerX: number;
  centerY: number;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  pushMagnitude: number;
  pushSpring: SpringConfig;
  radius: number;
  selectedColor: string | null;
  setSelectedColor: (color: string | null) => void;
}

function ColorDot({
  ring,
  index,
  totalInRing,
  centerX,
  centerY,
  pointerX,
  pointerY,
  pushMagnitude,
  pushSpring,
  radius,
  selectedColor,
  setSelectedColor,
}: ColorDotProps) {
  const baseRadius = ring * 20;
  const angle = calculateAngle(index, totalInRing);
  const { x: baseX, y: baseY } = calculateBasePosition(angle, baseRadius);

  let color = 'hsl(0, 0%, 100%)';
  let normalizedHue = 0;
  if (ring !== 0) {
    normalizedHue = calculateHue(angle);
    color =
      ring === 1
        ? `hsl(${normalizedHue}, 60%, 85%)`
        : `hsl(${normalizedHue}, 90%, 60%)`;
  }

  const pushDistance = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return 0;

      const dx = px - centerX;
      const dy = py - centerY;
      const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

      if (distanceFromCenter > radius + 40) return 0;

      const dotX = centerX + baseX;
      const dotY = centerY + baseY;

      const cursorToDotX = dotX - px;
      const cursorToDotY = dotY - py;
      const cursorToDotDistance = Math.sqrt(
        cursorToDotX * cursorToDotX + cursorToDotY * cursorToDotY
      );

      const minDistance = 80;
      if (cursorToDotDistance < minDistance) {
        const pushStrength = 1 - cursorToDotDistance / minDistance;
        return pushStrength * pushMagnitude;
      }

      return 0;
    }
  );

  const pushAngle = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return angle;

      const dotX = centerX + baseX;
      const dotY = centerY + baseY;

      const cursorToDotX = dotX - px;
      const cursorToDotY = dotY - py;

      return Math.atan2(cursorToDotY, cursorToDotX);
    }
  );

  const pushX = useTransform(
    [pushDistance, pushAngle],
    ([distance, ang]: number[]) => {
      return Math.cos(ang) * distance;
    }
  );

  const pushY = useTransform(
    [pushDistance, pushAngle],
    ([distance, ang]: number[]) => {
      return Math.sin(ang) * distance;
    }
  );

  const springPushX = useSpring(pushX, pushSpring);
  const springPushY = useSpring(pushY, pushSpring);

  const x = useTransform(springPushX, (val: number) => baseX + val);
  const y = useTransform(springPushY, (val: number) => baseY + val);

  const dotVariants = {
    default: {
      scale: 1,
    },
    hover: {
      scale: 1.5,
      transition: { duration: 0.13 },
    },
  };

  const ringVariants = {
    default: {
      opacity: 0,
    },
    hover: {
      opacity: 0.4,
      transition: { duration: 0.13 },
    },
  };

  const isSelected = selectedColor === color;

  return (
    <motion.div
      className='color-dot'
      style={{
        x,
        y,
        backgroundColor: color,
        boxShadow: isSelected
          ? '0 0 0 3px #ffffff, 0 0 0 5px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 2px 5px rgba(0, 0, 0, 0.25)',
        zIndex: isSelected ? 10 : 2,
        willChange: 'transform, background-color',
      }}
      variants={dotVariants}
      initial='default'
      whileHover='hover'
      whileTap={{ scale: 1.2 }}
      onClick={() => {
        if (selectedColor === color) {
          setSelectedColor(null);
        } else {
          setSelectedColor(color);
        }
      }}
      transition={{
        scale: { type: 'spring', damping: 30, stiffness: 200 },
      }}
    >
      <motion.div className='color-dot-ring' variants={ringVariants} />
    </motion.div>
  );
}

interface GradientCircleProps {
  index: number;
  totalInRing: number;
  centerX: number;
  centerY: number;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  containerRadius: number;
}

function GradientCircle({
  index,
  totalInRing,
  centerX,
  centerY,
  pointerX,
  pointerY,
  containerRadius,
}: GradientCircleProps) {
  const angle = calculateAngle(index, totalInRing);
  const baseRadius = containerRadius - 40;
  const { x: baseX, y: baseY } = calculateBasePosition(angle, baseRadius);
  const normalizedHue = calculateHue(angle);

  const gradient = `radial-gradient(circle, hsla(${normalizedHue}, 90%, 60%, 1) 0%, hsla(${normalizedHue}, 90%, 60%, 0) 66%)`;

  const proximity = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return 0;

      const gradientX = centerX + baseX;
      const gradientY = centerY + baseY;

      const dx = px - gradientX;
      const dy = py - gradientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const maxDistance = 100;
      return Math.max(0, 1 - distance / maxDistance);
    }
  );

  const opacity = useTransform(proximity, (val: number) => 0.15 + val * 0.2);
  const scale = useTransform(proximity, (val: number) => 1 + val * 0.2);

  const springOpacity = useSpring(opacity, {
    damping: 30,
    stiffness: 100,
  });
  const springScale = useSpring(scale, {
    damping: 30,
    stiffness: 100,
  });

  return (
    <motion.div
      className='gradient-circle'
      style={{
        x: baseX,
        y: baseY,
        opacity: springOpacity,
        scale: springScale,
        background: gradient,
        willChange: 'transform, opacity',
      }}
    />
  );
}

interface RadialMotionColorPickerProps {
  selectedColor: string | null;
  onSelectColor: (color: string | null) => void;
  pushMagnitude?: number;
  pushSpring?: SpringConfig;
}

export function RadialMotionColorPicker({
  selectedColor,
  onSelectColor,
  pushMagnitude = 5,
  pushSpring = {
    damping: 30,
    stiffness: 100,
  },
}: RadialMotionColorPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ centerX, centerY, radius }, setContainerDimensions] = useState({
    centerX: 0,
    centerY: 0,
    radius: 70,
  });

  const pointer = usePointerPosition();

  const updateDimensions = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerDimensions({
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        radius: rect.width / 2,
      });
    }
  };

  useLayoutEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('scroll', updateDimensions, true);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', updateDimensions, true);
    };
  }, []);

  const rings = [{ count: 1 }, { count: 6 }, { count: 12 }];

  const dots: Array<{
    ring: number;
    index: number;
    totalInRing: number;
  }> = [];

  rings.forEach((ring, ringIndex) => {
    for (let i = 0; i < ring.count; i++) {
      dots.push({
        ring: ringIndex,
        index: i,
        totalInRing: ring.count,
      });
    }
  });

  const originalStopValues: string[] = [];
  for (let i = 0; i <= 360; i += 30) {
    originalStopValues.push(`hsl(${i}, 90%, 60%)`);
  }

  const stopMotionValues = useRef(
    originalStopValues.map((value: string) => useMotionValue(value))
  ).current;

  useEffect(() => {
    if (selectedColor !== null) {
      for (const stopValue of stopMotionValues) {
        animate(stopValue, selectedColor, {
          duration: 0.2,
        });
      }
    } else {
      for (let i = 0; i < stopMotionValues.length; i++) {
        animate(stopMotionValues[i], originalStopValues[i], {
          duration: 0.2,
        });
      }
    }
  }, [selectedColor, stopMotionValues]);

  const gradientBackground = useTransform(
    stopMotionValues,
    (values: string[]) => {
      return `conic-gradient(from 0deg, ${values.join(', ')})`;
    }
  );

  const gradientScale = useMotionValue(1);

  useEffect(() => {
    if (selectedColor !== null) {
      animate(gradientScale, 1.1, {
        type: 'spring',
        damping: 18,
        stiffness: 220,
      });
    } else {
      animate(gradientScale, 1, {
        type: 'spring',
        damping: 20,
        stiffness: 200,
      });
    }
  }, [selectedColor, gradientScale]);

  return (
    <div className='gradient-wrapper'>
      <div className='background'>
        <motion.div
          className='gradient-background'
          style={{
            background: gradientBackground,
            scale: gradientScale,
          }}
        />
        <motion.div
          className='solid-background'
          animate={{
            scale: selectedColor !== null ? 0.9 : 0.98,
          }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200,
          }}
        />
      </div>

      <div
        ref={containerRef}
        className='picker-background'
        onMouseEnter={updateDimensions}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <GradientCircle
            key={`gradient-${index}`}
            index={index}
            totalInRing={6}
            centerX={centerX}
            centerY={centerY}
            pointerX={pointer.x}
            pointerY={pointer.y}
            containerRadius={radius}
          />
        ))}

        {dots
          .slice()
          .reverse()
          .map((dot) => (
            <ColorDot
              key={`${dot.ring}-${dot.index}`}
              ring={dot.ring}
              index={dot.index}
              totalInRing={dot.totalInRing}
              centerX={centerX}
              centerY={centerY}
              pointerX={pointer.x}
              pointerY={pointer.y}
              radius={radius}
              pushMagnitude={pushMagnitude}
              pushSpring={pushSpring}
              selectedColor={selectedColor}
              setSelectedColor={onSelectColor}
            />
          ))}
      </div>

      <StyleSheet />
    </div>
  );
}

/**
 * Stylesheet for the physics-based Motion Color Picker
 */
function StyleSheet() {
  return (
    <style>{`
      .gradient-wrapper {
        position: relative;
        width: 140px;
        height: 140px;
        display: flex;
        align-items: center;
        justify-content: center;
        user-select: none;
        touch-action: none;
      }

      .background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }

      .gradient-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        z-index: 0;
      }

      .solid-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: #0c121e;
        border-radius: 50%;
        z-index: 1;
      }

      .picker-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        overflow: visible;
        z-index: 2;
      }

      .color-dot {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 32px;
        height: 32px;
        margin-top: -16px;
        margin-left: -16px;
        border-radius: 50%;
        cursor: pointer;
      }

      .color-dot-ring {
        position: absolute;
        inset: 0;
        border: 2px solid white;
        border-radius: 50%;
        mix-blend-mode: overlay;
        pointer-events: none;
      }

      .gradient-circle {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 150px;
        height: 150px;
        margin-top: -75px;
        margin-left: -75px;
        border-radius: 50%;
        pointer-events: none;
        mix-blend-mode: screen;
      }
    `}</style>
  );
}

/**
 * ==============   Apple-Style Full Motion Color Picker Component   ================
 */

export const DEFAULT_SWATCHES = [
  { name: 'HiAcuity Emerald', hex: '#038912' },
  { name: 'Deep Forest', hex: '#093725' },
  { name: 'Apple Sapphire', hex: '#0071e3' },
  { name: 'Royal Indigo', hex: '#4f46e5' },
  { name: 'Electric Violet', hex: '#8b5cf6' },
  { name: 'Apple Rose', hex: '#ff2d55' },
  { name: 'Sunset Coral', hex: '#ff643d' },
  { name: 'Amber Gold', hex: '#f59e0b' },
  { name: 'Teal Cyan', hex: '#06b6d4' },
  { name: 'Space Slate', hex: '#1e293b' },
];

export interface SwatchItem {
  name: string;
  hex: string;
}

export interface MotionColorPickerProps {
  color?: string;
  onChange: (newColor: string) => void;
  isEditing?: boolean;
  label?: string;
  description?: string;
  presetColors?: SwatchItem[];
  defaultColor?: string;
  popoverAlign?: 'left' | 'right';
}

export const MotionColorPicker: React.FC<MotionColorPickerProps> = ({
  color,
  onChange,
  isEditing = true,
  label = 'Brand Color',
  description = 'Click to customize palette',
  presetColors = DEFAULT_SWATCHES,
  defaultColor = DEFAULT_PRIMARY_COLOR,
  popoverAlign = 'left',
}) => {
  const currentColor = isValidHex(color || '') ? normalizeHex(color || '') : defaultColor;
  const [isOpen, setIsOpen] = useState(false);
  const [inputHex, setInputHex] = useState(currentColor);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);

  const isCustomColor =
    normalizeHex(currentColor).toLowerCase() !==
    normalizeHex(defaultColor).toLowerCase();

  const handleResetToDefault = () => {
    onChange(defaultColor);
  };

  useEffect(() => {
    setInputHex(currentColor);
  }, [currentColor]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const contrastText = getContrastTextColor(currentColor);
  const contrastRatioWhite = getContrastRatio(currentColor, '#ffffff');
  const contrastRatioDark = getContrastRatio(currentColor, '#0f172a');
  const activeContrastRatio =
    contrastText === '#ffffff' ? contrastRatioWhite : contrastRatioDark;

  const currentHsl = hexToHsl(currentColor);

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputHex(val);
    if (isValidHex(val.trim())) {
      onChange(normalizeHex(val.trim()));
    }
  };

  const handleCopyHex = () => {
    navigator.clipboard.writeText(currentColor.toUpperCase());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSelectMotionDot = (hslColor: string | null) => {
    if (!hslColor) {
      onChange(defaultColor);
      return;
    }
    const hex = hslToHex(hslColor);
    onChange(hex);
  };

  const handleHueBarInteraction = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hueSliderRef.current) return;
    const rect = hueSliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const hue = (x / rect.width) * 360;

    const newHex = hslToHex(hue, 85, 45);
    onChange(newHex);
  };

  return (
    <div
      className={`relative inline-block w-full ${isOpen ? 'z-50' : 'z-10'}`}
      ref={containerRef}
    >
      {/* Apple-styled Trigger Bar */}
      <motion.div
        whileHover={isEditing ? { scale: 1.005, translateY: -1 } : {}}
        whileTap={isEditing ? { scale: 0.995 } : {}}
        onClick={() => isEditing && setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-3 p-3.5 bg-white/90 backdrop-blur-xl border border-slate-200/90 rounded-2xl shadow-sm transition-all duration-200 ${
          isEditing
            ? 'cursor-pointer hover:border-slate-300 hover:shadow-md'
            : 'cursor-default'
        }`}
      >
        <div className='flex items-center gap-3.5'>
          {/* Animated Color Swatch with Gloss Effect */}
          <motion.div
            layout
            className='relative w-11 h-11 rounded-xl shadow-md border border-black/10 flex items-center justify-center font-bold text-xs overflow-hidden flex-shrink-0'
            style={{
              backgroundColor: currentColor,
              color: contrastText,
            }}
          >
            <div className='absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-white/20 pointer-events-none' />
            <span className='relative z-10 font-bold tracking-tight'>Aa</span>
          </motion.div>

          <div className='space-y-0.5 text-left'>
            <div className='text-xs font-bold text-slate-800 tracking-tight flex items-center gap-2'>
              <span>{label}</span>
              {isEditing && (
                <span className='text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100'>
                  Customize
                </span>
              )}
            </div>
            <div className='text-xs font-mono font-medium text-slate-500 flex items-center gap-1.5'>
              <span className='font-semibold text-slate-700'>{currentColor.toUpperCase()}</span>
              <span className='text-slate-300'>•</span>
              <span className='text-[11px] font-sans text-slate-400'>
                {currentHsl.hslString}
              </span>
            </div>
          </div>
        </div>

        <div className='flex items-center gap-2.5'>
          {/* WCAG Contrast badge */}
          <div
            className='px-2.5 py-1 rounded-full text-[10px] font-bold tracking-tight border flex items-center gap-1.5 shadow-xs'
            style={{
              backgroundColor:
                contrastText === '#ffffff' ? '#0f172a' : '#f8fafc',
              color: contrastText === '#ffffff' ? '#ffffff' : '#0f172a',
              borderColor: 'rgba(0,0,0,0.08)',
            }}
          >
            <span className='w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse' />
            <span>WCAG {activeContrastRatio.toFixed(1)}:1</span>
          </div>

          {isEditing && (
            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className='p-1.5 rounded-lg bg-slate-100/80 text-slate-500'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Apple-styled Motion Color Picker Popover */}
      <AnimatePresence>
        {isOpen && isEditing && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ type: 'spring', damping: 25, stiffness: 320 }}
            className={`absolute z-[100] ${
              popoverAlign === 'right' ? 'right-0 left-auto' : 'left-0 right-auto'
            } mt-2.5 w-[360px] max-w-[92vw] bg-white/95 backdrop-blur-2xl border border-slate-200/90 rounded-3xl shadow-2xl p-5 flex flex-col gap-4 overflow-hidden`}
          >
            {/* Popover Header */}
            <div className='flex items-center justify-between border-b border-slate-100 pb-3'>
              <div className='flex items-center gap-2.5'>
                <div
                  className='w-3.5 h-3.5 rounded-full shadow-sm'
                  style={{ backgroundColor: currentColor }}
                />
                <div>
                  <h4 className='text-xs font-bold uppercase tracking-wider text-slate-800 text-left'>
                    {label}
                  </h4>
                  <p className='text-[11px] text-slate-400 text-left'>
                    Physics Color Spectrum
                  </p>
                </div>
              </div>

              <div className='flex items-center gap-1.5'>
                {isCustomColor && isEditing && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type='button'
                    onClick={handleResetToDefault}
                    title={`Reset to default (${defaultColor.toUpperCase()})`}
                    className='px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors flex items-center gap-1 shadow-2xs'
                  >
                    <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2.5' d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                    </svg>
                    <span>Reset</span>
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsOpen(false)}
                  className='w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-xs transition-colors'
                >
                  ✕
                </motion.button>
              </div>
            </div>

            {/* Clean Light Surface for Color Wheel (No dark background) */}
            <div className='flex flex-col items-center justify-center py-4 px-3 bg-slate-50/90 rounded-2xl border border-slate-200/80 shadow-inner relative overflow-hidden'>
              <div className='relative z-10 p-1'>
                <RadialMotionColorPicker
                  selectedColor={currentHsl.hslString}
                  onSelectColor={handleSelectMotionDot}
                />
              </div>
              <span className='mt-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest relative z-10'>
                Hover dots to repel with physics
              </span>
            </div>

            {/* Curated Swatches */}
            <div className='space-y-1.5 text-left'>
              <span className='text-[10px] font-bold text-slate-400 tracking-wider uppercase block'>
                Recommended Presets
              </span>
              <div className='grid grid-cols-5 gap-2'>
                {presetColors.map((item) => {
                  const isSelected =
                    currentColor.toLowerCase() === item.hex.toLowerCase();
                  return (
                    <motion.button
                      key={item.hex}
                      type='button'
                      onClick={() => onChange(item.hex)}
                      title={item.name}
                      whileHover={{ scale: 1.12 }}
                      whileTap={{ scale: 0.92 }}
                      className={`relative w-10 h-10 rounded-xl shadow-xs border transition-all flex items-center justify-center ${
                        isSelected
                          ? 'ring-2 ring-emerald-500 ring-offset-2 scale-105 border-transparent shadow-md'
                          : 'border-black/10 hover:shadow-sm'
                      }`}
                      style={{ backgroundColor: item.hex }}
                    >
                      {isSelected && (
                        <motion.svg
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className='w-4 h-4 drop-shadow-sm'
                          fill='none'
                          stroke={getContrastTextColor(item.hex)}
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={3}
                            d='M5 13l4 4L19 7'
                          />
                        </motion.svg>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Hue Spectrum Slider */}
            <div className='space-y-1 text-left'>
              <div className='flex items-center justify-between text-[10px] font-semibold text-slate-500'>
                <span className='tracking-wider uppercase'>Hue Spectrum</span>
                <span className='font-mono'>{currentHsl.h}°</span>
              </div>
              <div
                ref={hueSliderRef}
                onClick={handleHueBarInteraction}
                className='relative h-5 rounded-full cursor-pointer shadow-inner overflow-hidden border border-black/10'
                style={{
                  background:
                    'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                }}
              >
                <motion.div
                  className='absolute top-0 bottom-0 w-5 -ml-2.5 rounded-full border-2 border-white shadow-md bg-transparent pointer-events-none'
                  animate={{ left: `${(currentHsl.h / 360) * 100}%` }}
                  transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                />
              </div>
            </div>

            {/* HEX Input & Copy Action */}
            <div className='flex items-center gap-2 pt-2 border-t border-slate-100'>
              <div className='relative flex-1'>
                <span className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-xs font-bold'>
                  HEX
                </span>
                <input
                  type='text'
                  value={inputHex}
                  onChange={handleHexInputChange}
                  placeholder='#038912'
                  className='w-full pl-12 pr-3 py-1.5 text-xs font-mono font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white text-slate-800 transition-all'
                />
              </div>

              <motion.button
                type='button'
                onClick={handleCopyHex}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.92 }}
                className='px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors flex items-center gap-1.5 shadow-xs'
              >
                {copied ? (
                  <>
                    <svg
                      className='w-3.5 h-3.5 text-emerald-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2.5}
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    <span className='text-emerald-700 font-bold'>Copied</span>
                  </>
                ) : (
                  <>
                    <svg
                      className='w-3.5 h-3.5 text-slate-500'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                      />
                    </svg>
                    <span>Copy</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
